import { notifContext } from "configs/context/notifContext";
import { authContext } from "configs/context/authContext";
import { socket } from "core/utils/socketio";
import { getListUnreadMessageModel } from "models/messageModel";
import { getListUnreadProjectModel } from "models/projectModel";
import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const listModuleProtected = [
  { id: 1, title: "Users", to: "/users", notif: "users" },
  { id: 2, title: "Idea", to: "/idea/myidea", notif: "idea" },
  { id: 3, title: "Project", to: "/project", notif: "project" },
  { id: 4, title: "Agenda", to: "/agenda", notif: "agenda" },
  { id: 5, title: "Meeting", to: "/meeting", notif: "meeting" },
  { id: 6, title: "Memo", to: "/memo/request", notif: "memo" },
  { id: 7, title: "Message", to: "/message", notif: "message" },
  { id: 8, title: "Profile", to: "/profile", notif: "profile" },
];
const listModule = [
  { id: 8, title: "Profile", to: "/profile", notif: "profile" },
];

const Header = () => {
  const { user } = useContext(authContext);
  const { unreadModule, setUnreadModule } = useContext(notifContext);
  const { addAlert } = useContext(notifContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { handleLogoutUser } = useContext(authContext);
  const [activeTab, setActiveTab] = useState("Home");

  const handleTabClick = (e, menuItem) => {
    e.preventDefault();
    setActiveTab(menuItem.id);
    navigate(menuItem.to);
  };

  const MenuItem = ({ menuItem, notification }) => (
    <li className="nav-item nav-header d-none d-sm-inline-block">
      <Link
        style={{ height: "fit-content" }}
        onClick={(e) => handleTabClick(e, menuItem)}
        className={`nav-link p-1 ${
          activeTab === menuItem.id ? "nav-item-active" : "text-white"
        }`}
      >
        <p className="m-0">{menuItem.title}</p>
        {notification > 0 && (
          <span
            style={{ top: 0, right: 0 }}
            className="position-absolute  translate-middle badge rounded-pill bg-warning"
          >
            {notification}
          </span>
        )}
      </Link>
    </li>
  );

  const DropdownMenuItem = ({ menuItem }) => (
    <Link
      onClick={(e) => handleTabClick(e, menuItem)}
      className={`nav-link p-1 dropdown-item ${
        activeTab === menuItem.title ? "nav-item-active" : "primary-color"
      }`}
    >
      {menuItem.title}
    </Link>
  );

  // 🔄 Fetch unread counts
  const refreshUnreadCount = useCallback(async () => {
    if (!user?.token) return;

    try {
      const [messagesResp, projectsResp] = await Promise.all([
        getListUnreadMessageModel(user.phoneNumber),
        getListUnreadProjectModel(user.token),
      ]);

      setUnreadModule({
        message: messagesResp?.success ? messagesResp.data : [],
        project: projectsResp?.success ? projectsResp.data : [],
      });
    } catch (err) {
      console.error("Failed to refresh unread counts:", err);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user) refreshUnreadCount();
  }, [user]);

  useEffect(() => {
    if (!socket) return;

    const handleNewProject = (project) => {
      addAlert({
        title: "Project",
        html: `<div class="d-flex flex-column mt-2">
        <p class="m-0 text-bold text-md swal2-html-title-custom-notif bg-success rounded p-1">You added to a project</p>
        <p class="m-0 text-bold text-md swal2-html-title-custom-notif">Project Name : ${project.name}</p>
        <p class="m-0  text-md swal2-html-title-custom-notif">Added By : ${project.addedBy?.username}</p>
        `,
        path: `/project/${project.id}`,
      });
      setUnreadModule((prev) => {
        const current =
          prev.project?.filter((item) => item !== Number(project.id)) ?? [];

        return {
          ...prev,
          project: [...current, Number(project.id)], // add
        };
      });
    };
    const handleNewReport = (report) => {
      addAlert({
        title: "Project",
        html: `<div class="d-flex flex-column mt-2">
        <p  class="m-0 text-bold text-md swal2-html-title-custom-notif bg-warning rounded p-1">You have a tagged report </p>
        <p class="m-0 text-bold text-md swal2-html-title-custom-notif">Project Name : ${report.projectName}</p>
        <p class="m-0 text-md swal2-html-title-custom-notif">Report By : ${report.owner?.username}</p>
        <p class="m-0 text-md swal2-html-title-custom-notif">Content : ${report.content}</p>`,
        path: `/project/${report.id}`,
      });
      setUnreadModule((prev) => {
        const current =
          prev.project?.filter((item) => item !== Number(report.id)) ?? [];

        return {
          ...prev,
          project: [...current, Number(report.id)], // add
        };
      });
    };
    const handleRemoveProject = (project) => {
      addAlert({
        title: "Project",
        html: `<div class="d-flex flex-column mt-2">
        <p class="m-0 text-bold text-md swal2-html-title-custom-notif bg-danger rounded p-1">You removed from a project</p>
        <p class="m-0 text-bold text-md swal2-html-title-custom-notif">Project Name : ${project.name}</p>
        <p class="m-0 text-md swal2-html-title-custom-notif">Removed By : ${project.removedBy?.username}</p>`,
        path: "/project",
      });
    };

    socket.on("project:new", handleNewProject);
    socket.on("project:added", handleNewProject);
    socket.on("project:removed", handleRemoveProject);
    socket.on("project:report:new", handleNewReport);

    return () => {
      socket.off("project:new", handleNewProject);
      socket.off("project:added", handleNewProject);
      socket.off("project:removed", handleRemoveProject);
      socket.off("project:report:new", handleNewReport);
    };
  }, []);

  useEffect(() => {
    if (location.pathname.includes("/users")) setActiveTab(1);
    else if (location.pathname.includes("/idea")) {
      setActiveTab(2);
    } else if (location.pathname.includes("/project")) {
      setActiveTab(3);
    } else if (location.pathname.includes("/agenda")) {
      setActiveTab(4);
    } else if (location.pathname.includes("/meeting")) {
      setActiveTab(5);
    } else if (location.pathname.includes("/memo")) {
      setActiveTab(6);
    } else if (location.pathname.includes("/message")) {
      setActiveTab(7);
    }
  }, [location]);

  return (
    <nav className="main-header navbar navbar-expand primary-bg navbar-light fixed-top">
      {/* Sidebar toggle */}
      <ul className="navbar-nav">
        <a
          className="nav-link"
          data-widget="pushmenu"
          href="#"
          role="button"
          id="toggle-sidebar"
        >
          <i
            className="fas fa-bars"
            style={{ fontSize: "22px", color: "white" }}
          />
        </a>
      </ul>

      {/* Menu items */}
      <ul
        className="navbar-nav ml-auto d-flex align-items-center"
        style={{ gap: "1rem" }}
      >
        {listModuleProtected
          .filter((m) => user?.modules?.includes(m.id))
          ?.map((menuItem) => (
            <MenuItem
              notification={unreadModule[menuItem.title.toLowerCase()]?.length}
              key={menuItem.id}
              menuItem={menuItem}
            />
          ))}
        {listModule?.map((menuItem) => (
          <MenuItem key={menuItem.id} menuItem={menuItem} />
        ))}
        {/* Logout button */}
        <li className="nav-item d-none d-sm-inline-block">
          <button
            onClick={handleLogoutUser}
            className="nav-link btn btn-link p-0"
            style={{ color: "white" }}
          >
            <i className="nav-icon fas fa-sign-out-alt" />
          </button>
        </li>
        {/* Mobile dropdown */}
        <li className="navbar-item dropdown d-flex d-sm-none ml-auto">
          <a
            className="nav-link text-white"
            href="#"
            id="navbarDropdown"
            role="button"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <i className="fas fa-ellipsis-v" />
          </a>
          <div
            className="dropdown-menu dropdown-menu-lg dropdown-menu-right p-2"
            aria-labelledby="navbarDropdown"
          >
            {listModuleProtected
              .filter((m) => user?.modules?.includes(m.id))
              ?.map((menuItem) => (
                <DropdownMenuItem key={menuItem.id} menuItem={menuItem} />
              ))}
          </div>
        </li>
      </ul>
    </nav>
  );
};

export default Header;
